// noinspection ES6PreferShortImport,JSUnusedGlobalSymbols

import { ApiKeyMode, UserRole } from './Types'
import { CardBrand, CouponDuration, InvoiceStatus, SubscriptionInterval, SubscriptionStatus } from '../extern/StripeTypes'
import { ApiClientData, ApiEventSource, ApiEventSubData, ApiEventType } from './ApiEvents'
import { Equipment, EquipmentType } from './EquipmentTypes'

/**
 * NOTE: API interface types cannot use Firebase Timestamp types as they do not automatically
 *       serialize to the appropriate object types when converting to/from JSON. Instead,
 *       for API types, simple milliseconds will be used
 */

export interface TimestampMillisData {
    createdMillis: number
}

export interface Subscription {
    id: string
    data: SubscriptionData
}

export interface SubscriptionData extends TimestampMillisData {
    stripeCustomerId: string
    status: SubscriptionStatus
    interval: SubscriptionInterval
    intervalCount: number
    stripeSubscriptionItemId: string
    stripeProductId: string
    stripePriceId: string
    quantity: number
    currentPeriodStartMillis: number
    currentPeriodEndMillis: number
    cancelAtMillis?: number
    cancelAtPeriodEnd: boolean
    canceledAtMillis?: number
}

export interface Coupon {
    id: string
    data: CouponData
}

export interface CouponData extends TimestampMillisData {
    isValid: boolean                    // true if this can still be applied to a customer
    name: string                        // visible to the customer on the invoice
    companyName: string | null          // useful to place company name
    amountOff: number | null            // positive integer representing cents in currency
    amountOffCurrency: string | null    // USD to use dollars
    maxRedemptions: number | null       // max times this can be redeemed
    timesRedeemed: number               // number of times redeemed
    duration: CouponDuration
    durationMonths?: number             // only valid if duration = 'repeating'
}

export interface Invoice {
    id: string
    data: InvoiceData
}

export interface InvoiceData extends TimestampMillisData {
    stripeCustomerId: string
    stripeInvoiceNumber: string | null
    amountPaid: number
    amountRemaining: number
    attempted: boolean
    attemptCount: number
    currency: string
    dueMillis: number | null
    endingBalance: number | null
    hostedInvoiceUrl?: string | null
    invoicePdf?: string | null
    paid: boolean
    status: InvoiceStatus | null
    total: number
}

export interface PaymentMethod {
    id: string
    data: PaymentMethodData
}

export interface PaymentMethodData extends TimestampMillisData {
    stripeCustomerId: string | null
    cardBrand?: CardBrand
    cardLast4?: string
    expireMonth?: number
    expireYear?: number
    postalCode?: string
}

export interface TaxRate {
    id: string
    data: TaxRateData
}

export interface TaxRateData extends TimestampMillisData {
    displayName: string;
    inclusive: boolean;             // This specifies if the tax rate is inclusive or exclusive
    jurisdiction: string | null;    // The jurisdiction for the tax rate (US-TN)
    percentage: number;             // This represents the tax rate percent out of 100
}

export interface CreateUserRequest {
    firstName: string
    lastName: string
    email: string
    password: string
    companyId?: string
    role: UserRole
    isTester?: boolean              // true for permanent test accounts
    isTemp?: boolean                // true for temporary accounts
    tempAccessCodeId?: string       // end for temp accounts
}

export interface CreateUserResponse {
    id: string
}

export interface EditUserRequest {
    userId: string
    newFirstName?: string               // if first name is specified, last name must be specified
    newLastName?: string                // if last name is specified, first name must be specified
    newEmail?: string
    newRole?: UserRole
}

export interface DeleteUserRequest {
    userId: string
}

export interface ManageUserSubscriptionRequest {
    userId: string
    subscriptionId: string
    type: ManageUserSubscriptionType
}

export enum ManageUserSubscriptionType {
    ASSIGN = 'ASSIGN',
    UNASSIGN = 'UNASSIGN'
}

export interface UserSubscriptionStatusResponse {
    userId: string
    isSubscriptionRequired: boolean
    isSubscriptionActive: boolean
    subscriptionEndMillis: number
}

export interface CreateAccessCodeRequest {
    name: string
    accessEndMillis: number          // end time for access in milliseconds
    accessCodeId: string
}

export interface CreateAccessCodeResponse {
    accessCodeId: string
}

export interface EditAccessCodeRequest {
    accessCodeId: string
    newName?: string
    newAccessEndMillis?: number
}

export interface DeleteAccessCodeRequest {
    accessCodeId: string
}

export interface CreateTempUserCredentialsCodeRequest {
    accessCodeId: string
}

export interface CreateTempUserCredentialsCodeResponse {
    userId: string
    email: string
    password: string
}

export interface CreateCompanyRequest {
    admin: CreateCompanyRequestAdmin
    company: CreateCompanyRequestCompany
}

export interface CreateCompanyRequestAdmin {
    firstName: string
    lastName: string
    email: string
    password: string
}

export interface CreateCompanyRequestCompany {
    name: string
    email: string
}

export interface CreateCompanyResponse {
    userId: string
    companyId: string
}

export interface EditCompanyRequest {
    companyId: string
    stripeCustomerId?: string // not required to be populated by the user
    newName?: string
    newEmail?: string
}

export interface QuerySubscriptionsRequest {
    companyId?: string
    stripeCustomerId?: string
    startAfterStripeSubscriptionId?: string
    includeCanceled?: boolean
    limit?: number
}

export interface QuerySubscriptionsResponse {
    subscriptions: Subscription[]
    hasMore: boolean
}

export interface CreateSubscriptionRequest {
    companyId: string
    stripeCustomerId: string
    stripePriceId: string
    quantity: number
    stripePaymentMethodId: string
    stripeCouponId?: string
}

export interface EditSubscriptionQuantityRequest {
    stripeCustomerId: string
    stripeSubscriptionId: string
    stripeSubscriptionItemId: string
    quantity: number
}

export interface CancelSubscriptionRequest {
    stripeCustomerId: string
    stripeSubscriptionId: string
}

export interface CreateCouponRequest {
    name: string                    // visible to the customer on the invoice
    companyName?: string            // useful to place company name
    amountOff: number               // positive integer representing cents in currency
    amountOffCurrency: string       // USD to use dollars
    maxRedemptions?: number
    duration: CouponDuration
    durationMonths?: number         // only required if duration = 'repeating'
}

export interface DeleteCouponRequest {
    stripeCouponId: string
}

export interface QueryCouponsRequest {
    startAfterStripeCouponId?: string
    limit?: number
}

export interface QueryCouponsResponse {
    coupons: Coupon[]
    hasMore: boolean
}

export interface QueryInvoicesRequest {
    companyId?: string
    stripeCustomerId?: string
    startAfterStripeInvoiceId?: string
}

export interface QueryInvoicesResponse {
    invoices: Invoice[]
    hasMore: boolean
}

export interface QueryPaymentMethodsRequest {
    companyId: string
    stripeCustomerId?: string // not required to be populated by the user
    startAfterStripePaymentMethodId?: string
    limit?: number
}

export interface QueryPaymentMethodsResponse {
    paymentMethods: PaymentMethod[]
    hasMore: boolean
}

export interface SetDefaultPaymentMethodRequest {
    companyId: string
    stripePaymentMethodId: string
}

export interface VerifiedSetDefaultPaymentMethodRequest extends SetDefaultPaymentMethodRequest {
    stripeCustomerId: string
}

export interface AttachPaymentMethodRequest {
    companyId: string
    stripePaymentMethodId: string
    isDefaultPaymentMethod?: boolean
}

export interface VerifiedAttachPaymentMethodRequest extends AttachPaymentMethodRequest {
    stripeCustomerId: string
}

export interface TaxRatesResponse {
    taxRates: TaxRate[]
}

export interface CreateApiKeyRequest {
    companyId: string
    mode: ApiKeyMode
    name: string
}

export interface CreateApiKeyResponse {
    apiKey: string
    companyId: string
    mode: ApiKeyMode
    name: string
}

export interface EditApiKeyRequest {
    apiKey: string
    newIsActive?: boolean
    newName?: string
}

export interface ValidateApiKeyRequest {
    apiKey: string
}

export interface ValidateApiKeyResponse {
    isValid: boolean
    companyId?: string                  // only populated if key is valid
}

export interface LogApiEventRequest {
    apiKey: string;
    type: ApiEventType;
    source: ApiEventSource;
    sourceTimeMillis: number;
    client: ApiClientData;
    traceId?: string;                   // optional to correlate INIT -> CALC events
    data?: ApiEventSubData;
}

export interface QueryApiEventRequestBase {
    apiKeyMode?: ApiKeyMode
    createdMillisGreaterThanOrEqualTo?: number
    createdMillisLessThan?: number
}

export interface QueryApiEventJobCountsRequest extends QueryApiEventRequestBase {
    partnerCompanyId?: string
}

export interface QueryApiEventJobCountsResponse {
    companyJobCounts: CompanyJobCountData[]
    eventTypeCounts: ApiEventTypeCountData[]
}

export interface CompanyJobCountData {
    companyId: string
    jobCount: number
    eventCount: number
}

export interface ApiEventTypeCountData {
    type: ApiEventType
    eventCount: number
}

export interface QueryEquipmentRequest {
    equipmentType?: EquipmentType
}

export interface QueryEquipmentResponse {
    equipment: Equipment[]
}

export interface GetEquipmentResponse {
    equipment: Equipment
}